/**
 * Shared With Me Page
 *
 * Displays links and accounts that other users have shared with the current user.
 * Allows viewing shared analytics without edit permissions.
 *
 * @module pages/shared
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  Link2,
  Globe,
  ExternalLink,
  Clock,
  AlertCircle,
  Loader2,
  CheckCircle,
  User,
  Folder,
} from 'lucide-react';
import { apiFetch } from '@/client/lib/api';
import { formatDate } from '@/client/lib/utils';

interface Share {
  id: string;
  ownerEmail: string;
  ownerName: string | null;
  scopeType: 'link' | 'account' | 'folder';
  linkId: string | null;
  linkTitle: string | null;
  linkShortCode: string | null;
  folderId: string | null;
  folderName: string | null;
  folderColor: string | null;
  folderLinkCount: number | null;
  status: 'pending' | 'active';
  createdAt: string;
  acceptedAt: string | null;
  expiresAt: string | null;
}

interface SharesResponse {
  shares: Share[];
  pendingCount: number;
}

export function SharedPage() {
  const { data, isLoading, error } = useQuery<SharesResponse>({
    queryKey: ['shares', 'received'],
    queryFn: async () => {
      const response = await apiFetch('/api/shares/received');
      if (!response.ok) throw new Error('Failed to fetch shares');
      return response.json();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-muted-foreground mb-4">Failed to load shared links</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  // Data is guaranteed to exist after loading/error checks above
  const shares = data?.shares ?? [];
  const pendingCount = data?.pendingCount ?? 0;
  const activeShares = shares.filter((s) => s.status === 'active');
  const pendingShares = shares.filter((s) => s.status === 'pending');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Shared with Me</h1>
        <p className="text-muted-foreground">
          View analytics for links that others have shared with you
        </p>
      </div>

      {/* Pending Invitations Banner */}
      {pendingShares.length > 0 && (
        <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-4">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-amber-500 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800 mb-1">
                {pendingCount} Pending Invitation{pendingCount !== 1 ? 's' : ''}
              </h3>
              <p className="text-sm text-amber-700 mb-3">
                You have pending share invitations waiting for your acceptance.
              </p>
              <div className="space-y-2">
                {pendingShares.map((share) => (
                  <PendingShareCard key={share.id} share={share} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Shares */}
      {activeShares.length === 0 && pendingShares.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-4">
          {activeShares.length > 0 && (
            <>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Active Shares ({activeShares.length})
              </h2>
              <div className="grid gap-4 md:grid-cols-2">
                {activeShares.map((share) => (
                  <ShareCard key={share.id} share={share} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ShareCard({ share }: { share: Share }) {
  const isExpired = share.expiresAt && new Date(share.expiresAt) < new Date();

  // Determine icon based on scope type
  const getIcon = () => {
    if (share.scopeType === 'folder') {
      return (
        <div
          className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: share.folderColor ? `${share.folderColor}20` : undefined }}
        >
          <Folder className="h-6 w-6" style={{ color: share.folderColor || undefined }} />
        </div>
      );
    }
    if (share.scopeType === 'account') {
      return (
        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Users className="h-6 w-6 text-primary" />
        </div>
      );
    }
    return (
      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <Link2 className="h-6 w-6 text-primary" />
      </div>
    );
  };

  // Determine title based on scope type
  const getTitle = () => {
    if (share.scopeType === 'folder') {
      return share.folderName || 'Unnamed Folder';
    }
    if (share.scopeType === 'account') {
      return `${share.ownerName || share.ownerEmail}'s Dashboard`;
    }
    return share.linkTitle || `arml.ink/${share.linkShortCode}`;
  };

  // Determine subtitle based on scope type
  const getScopeLabel = () => {
    if (share.scopeType === 'folder') {
      return `Folder · ${share.folderLinkCount ?? 0} ${share.folderLinkCount === 1 ? 'link' : 'links'}`;
    }
    if (share.scopeType === 'account') {
      return 'All Links';
    }
    return 'Single Link';
  };

  return (
    <Link
      to={`/shared/${share.id}`}
      className="block rounded-lg border bg-card p-5 hover:border-primary/50 transition-colors"
    >
      <div className="flex items-start gap-4">
        {getIcon()}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-semibold truncate">{getTitle()}</h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                <User className="h-3.5 w-3.5" />
                <span className="truncate">{share.ownerName || share.ownerEmail}</span>
              </div>
            </div>

            {share.scopeType === 'link' && share.linkShortCode && (
              <a
                href={`https://arml.ink/${share.linkShortCode}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-md hover:bg-muted transition-colors shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>

          <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
            <span className="capitalize px-2 py-0.5 rounded-full bg-muted">{getScopeLabel()}</span>
            {share.expiresAt && (
              <span className={isExpired ? 'text-destructive' : ''}>
                {isExpired ? 'Expired' : `Expires ${formatDate(share.expiresAt)}`}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

function PendingShareCard({ share }: { share: Share }) {
  const handleAccept = async () => {
    try {
      const response = await apiFetch(`/api/shares/${share.id}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to accept share:', error);
    }
  };

  // Determine icon based on scope type
  const getIcon = () => {
    if (share.scopeType === 'folder') {
      return <Folder className="h-4 w-4" style={{ color: share.folderColor || undefined }} />;
    }
    if (share.scopeType === 'account') {
      return <Users className="h-4 w-4 text-amber-600" />;
    }
    return <Link2 className="h-4 w-4 text-amber-600" />;
  };

  // Determine title based on scope type
  const getTitle = () => {
    if (share.scopeType === 'folder') {
      return share.folderName || 'Unnamed Folder';
    }
    if (share.scopeType === 'account') {
      return `${share.ownerName || share.ownerEmail}'s Dashboard`;
    }
    return share.linkTitle || `arml.ink/${share.linkShortCode}`;
  };

  return (
    <div className="flex items-center justify-between gap-4 p-3 rounded-md bg-muted/50">
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
          style={{
            backgroundColor:
              share.scopeType === 'folder' && share.folderColor
                ? `${share.folderColor}20`
                : 'rgb(var(--amber-500) / 0.2)',
          }}
        >
          {getIcon()}
        </div>
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">{getTitle()}</p>
          <p className="text-xs text-muted-foreground truncate">
            From {share.ownerName || share.ownerEmail}
          </p>
        </div>
      </div>
      <button
        onClick={handleAccept}
        className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shrink-0"
      >
        Accept
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-lg border bg-card p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
        <Globe className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-2">No shared links yet</h3>
      <p className="text-muted-foreground mb-4 max-w-md mx-auto">
        When someone shares their link analytics with you, they&apos;ll appear here. You&apos;ll be
        able to view their click metrics and performance data.
      </p>
      <Link
        to="/links"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
      >
        <Link2 className="h-4 w-4" />
        View Your Links
      </Link>
    </div>
  );
}
